import { Box, MenuItem, Skeleton, Typography } from "@mui/material";
import { Track } from "../../../types";
import ListingEmptyState from "../common/empty-state";
import AddHospitalityListing from "./hospitality-listing-forms/add-hospitality-listing";
import EditHospitalityListingModal from "./hospitality-listing-forms/edit-hospitality-listing";
import { useGetLatestHospitalityListing } from "../../../hooks/listings/get-hospitality-listing.hook";
import HospitalityListingDetails from "./hospitality-listing-details";
import MoreActionsMenu from "@/modules/common/components/ui-kit/menus/more-actions-menu";
import { Pencil } from "lucide-react";
import { useState } from "react";
import { useReadonlyContext } from "@/modules/common/context/read-only.context";
import Status from "@/modules/common/components/ui-kit/status/status";

export default function HospitalityActiveListingTab({
  track,
}: {
  track: Track;
}) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const { readonly } = useReadonlyContext();

  const { data, isLoading } = useGetLatestHospitalityListing({
    propertyUid: track.property.uid,
  });

  if (isLoading) return <Skeleton />;

  if (!data) {
    return (
      <ListingEmptyState
        callToAction={<AddHospitalityListing track={track} />}
      />
    );
  }

  return (
    <Box
      sx={(t) => ({
        display: "flex",
        flexDirection: "column",
        gap: t.nawy.spacing.md,
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          p: theme.nawy.spacing.md,
          backgroundColor: theme.nawy.colors.semantic.layer.primary,
          justifyContent: "space-between",
          borderRadius: theme.nawy.radius.xl,
        })}
      >
        <Box
          sx={(theme) => ({
            display: "flex",
            gap: theme.nawy.spacing.xs,
            flex: 1,
            alignItems: "center",
          })}
        >
          <Typography
            sx={(theme) => theme.nawy.typography.english.variants.headline.sm}
          >
            {data.name}
          </Typography>
          <Status
            label={data.isActive ? "Active" : "Inactive"}
            variant="secondary"
            color={data.isActive ? "green" : "red"}
            width="auto"
          />
          {data.isFeatured && (
            <Status
              label="Featured"
              variant="secondary"
              color="blue"
              width="auto"
            />
          )}
        </Box>
        <Box sx={(theme) => ({ display: "flex", gap: theme.nawy.spacing.xs })}>
          <MoreActionsMenu>
            <MenuItem
              disabled={readonly}
              sx={(theme) => ({
                display: "flex",
                gap: theme.nawy.spacing.xs,
              })}
              onClick={() => setEditModalOpen(true)}
            >
              <Pencil />
              Edit Listing
            </MenuItem>
          </MoreActionsMenu>
        </Box>
      </Box>

      <HospitalityListingDetails listing={data} />

      <EditHospitalityListingModal
        listing={data}
        open={editModalOpen}
        trackUid={track.uid}
        handleOpen={() => setEditModalOpen(true)}
        handleClose={() => setEditModalOpen(false)}
      />
    </Box>
  );
}
